import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { sendVerifyCode } from 'services/auth-service';
import { getCompanyShortBySlug } from 'services/company-service';
import {
  LoginPage,
  LoginCard,
  LogoWrapper,
  LoginHeading,
  FieldGroup,
  FieldLabel,
  FieldError,
  SubmitButton,
  FooterText,
} from './styles';
import { DotsLoader } from 'Components/DotsLoader';
import { getTenant } from 'utils/tenant_utils';
import { Dialog, TextField } from 'Components/ui';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';

export const Login = () => {
  const { t } = useTranslation(['user']);
  const dispatch = useDispatch();
  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
    setError,
  } = useForm({
    defaultValues: { email: '' },
  });
  const navigate = useNavigate();
  const { data: company, error } = useQuery(['getCompanyShortBySlug'], async () => {
    const response = await getCompanyShortBySlug(getTenant());
    return response.data;
  });
  const [oldErrorContent, setOldErrorContent] = useState(error);

  const mutation = useMutation(
    email => {
      if (company == null) throw new Error(t('user:login.error.companyNotFound'));
      return sendVerifyCode(email, company.slug);
    },
    {
      onError: error => {
        const errorMessage = error.response.data.message || error.message;
        setError('email', { type: 'custom', message: errorMessage });
      },
      onSuccess: data => {
        dispatch(updateTenant({ tenant: company }));
        navigate(`/verify-code/${data.data.email}`);
      },
    },
  );

  const onSubmit = () => {
    const values = getValues();
    mutation.mutate(values.email);
  };

  useEffect(() => {
    if (error !== null && error !== oldErrorContent) {
      setOldErrorContent(error);
      Dialog.alert({ title: 'Erro!', message: `${error.message}`, icon: 'error' });
    }
  }, [error, oldErrorContent]);

  return (
    <LoginPage>
      <LoginCard>
        <LogoWrapper>
          <img src="/img/logo-horizontal.png" height="40" alt="Virtual Officer" style={{ objectFit: 'contain' }} />
        </LogoWrapper>
        <LoginHeading>
          <h1>{t('user:login.heading')}</h1>
          <p>{t('user:login.subtitle')}</p>
        </LoginHeading>
        <FieldGroup>
          <FieldLabel htmlFor="login-email">{t('user:login.emailLabel')}</FieldLabel>
          <TextField
            id="login-email"
            type="email"
            fullWidth
            placeholder={t('user:login.placeholder')}
            error={!!errors?.email}
            {...register('email', { required: t('user:users.form.validation.emailRequired') })}
          />
          {errors?.email && <FieldError>{errors.email.message}</FieldError>}
        </FieldGroup>
        <SubmitButton type="submit" disabled={mutation.isLoading} onClick={handleSubmit(onSubmit)}>
          {mutation.isLoading ? <DotsLoader /> : t('user:login.submit')}
        </SubmitButton>
        <FooterText>
          {t('user:login.noAccount')}{' '}
          <button type="button" onClick={() => navigate('/create-account')}>
            {t('user:login.signup')}
          </button>
        </FooterText>
      </LoginCard>
    </LoginPage>
  );
};
